import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Todo, RecurrenceRule, RecurrenceType, OnComplete } from '../../types/todo';
import Modal from '../common/Modal';
import TimeScrollPicker from '../common/TimeScrollPicker';
import ErrorBanner from '../common/ErrorBanner';
import { formatTime12h } from '../../utils/dateUtils';
import { RemainingCount, TASK_TITLE_MAX } from '../common/HabitModals/HabitFormFields';
import './TodoModal.scss';

interface Props {
  todo: Todo | null;           // null = creating new
  seriesRule?: RecurrenceRule | null;
  defaultDate: string;         // YYYY-MM-DD
  defaultTime?: string;
  onSave: (data: {
    title: string;
    scheduledTime: string | null;
    recurrenceRule: RecurrenceRule | null;
    onComplete: OnComplete;
  }) => void | Promise<unknown>;
  onUpdate?: (todo: Todo) => void | Promise<unknown>;
  onDelete?: (id: string) => void;
  onDeleteSeries?: (parentId: string) => void;
  onClose: () => void;
}

const RECURRENCE_TYPES: (RecurrenceType | 'none')[] = ['none', 'daily', 'weekly', 'biweekly', 'monthly', 'interval'];

const DAY_LABEL_IDS = [
  'daily.todoModal.daySu',
  'daily.todoModal.dayMo',
  'daily.todoModal.dayTu',
  'daily.todoModal.dayWe',
  'daily.todoModal.dayTh',
  'daily.todoModal.dayFr',
  'daily.todoModal.daySa',
] as const;

const RECURRENCE_LABEL_IDS: Record<RecurrenceType | 'none', string> = {
  none: 'daily.todoModal.recNone',
  daily: 'daily.todoModal.recDaily',
  weekly: 'daily.todoModal.recWeekly',
  biweekly: 'daily.todoModal.recBiweekly',
  monthly: 'daily.todoModal.recMonthly',
  interval: 'daily.todoModal.recCustom',
};

const TodoModal: React.FC<Props> = ({
  todo,
  seriesRule = null,
  defaultDate,
  defaultTime = '',
  onSave,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const { formatMessage: t } = useIntl();
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [recType, setRecType] = useState<RecurrenceType | 'none'>('none');
  const [recDays, setRecDays] = useState<number[]>([]);
  const [recDayOfMonth, setRecDayOfMonth] = useState(1);
  const [recEvery, setRecEvery] = useState(2);
  const [recUnit, setRecUnit] = useState<'day' | 'week'>('day');
  const [onComplete, setOnComplete] = useState<OnComplete>('archive');
  const [titleError, setTitleError] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  useEffect(() => {
    if (todo) {
      setTitle(todo.title);
      setTime(todo.scheduledTime ?? '');
      setOnComplete(todo.onComplete);
      if (seriesRule) {
        setRecType(seriesRule.type);
        setRecDays(seriesRule.days ?? []);
        setRecDayOfMonth(seriesRule.dayOfMonth ?? 1);
        setRecEvery(seriesRule.every ?? 2);
        setRecUnit(seriesRule.unit ?? 'day');
      } else {
        setRecType('none');
      }
    } else {
      setTitle('');
      setTime(defaultTime);
      setOnComplete('archive');
      setRecType('none');
      setRecDays([]);
    }
    setTitleError(false);
    setValidationMessage(null);
  }, [defaultTime, seriesRule, todo]);

  const buildRecurrenceRule = (): RecurrenceRule | null => {
    if (recType === 'none') return null;
    const rule: RecurrenceRule = { type: recType };
    if (recType === 'weekly' || recType === 'biweekly') {
      rule.days = recDays.length > 0 ? recDays : [new Date(`${defaultDate}T00:00:00`).getDay()];
    }
    if (recType === 'monthly') {
      rule.dayOfMonth = recDayOfMonth;
    }
    if (recType === 'interval') {
      rule.every = Math.max(1, recEvery);
      rule.unit = recUnit;
    }
    return rule;
  };

  const handleSubmit = async () => {
    if (timePickerOpen) return;
    if (!title.trim()) {
      setTitleError(true);
      setValidationMessage(t({ id: 'daily.todoModal.requiredTitle' }));
      return;
    }

    setTitleError(false);
    setValidationMessage(null);

    if (todo && onUpdate) {
      await onUpdate({
        ...todo,
        title: title.trim(),
        scheduledTime: time || null,
        recurrenceRule: buildRecurrenceRule(),
        onComplete,
        updatedAt: new Date().toISOString(),
      });
    } else {
      await onSave({
        title: title.trim(),
        scheduledTime: time || null,
        recurrenceRule: buildRecurrenceRule(),
        onComplete,
      });
    }
    onClose();
  };

  const toggleDay = (d: number) => {
    setRecDays(prev => (prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]));
  };

  const handleDelete = todo && onDelete
    ? () => {
        onDelete(todo.id);
        onClose();
      }
    : undefined;

  return (
    <Modal
      title={todo ? t({ id: 'daily.todoModal.editTitle' }) : t({ id: 'daily.todoModal.newTitle' })}
      onClose={onClose}
      maxWidth={500}
      panelClassName="todo-modal"
      bodyClassName="todo-modal__body"
      onDelete={handleDelete}
      onSave={handleSubmit}
      saveDisabled={timePickerOpen}
      saveLabel={todo ? t({ id: 'daily.todoModal.save' }) : t({ id: 'daily.todoModal.create' })}
      saveType="submit"
      saveFormId="todo-modal-form"
    >
      <form id="todo-modal-form" className="todo-modal__form">
          {validationMessage ? <ErrorBanner message={validationMessage} /> : null}

          {/* Title */}
          <label className="tdm-field">
            <span className="tdm-label">{t({ id: 'daily.todoModal.fieldTitle' })}</span>
            <div className="field-with-count">
              <input
                className={`tdm-input${titleError ? ' tdm-input--error' : ''}`}
                type="text"
                placeholder={t({ id: 'daily.todoModal.titlePlaceholder' })}
                value={title}
                maxLength={TASK_TITLE_MAX}
                onChange={e => {
                  setTitle(e.target.value.slice(0, TASK_TITLE_MAX));
                  setTitleError(false);
                  setValidationMessage(null);
                }}
                autoFocus
              />
              <RemainingCount length={title.length} max={TASK_TITLE_MAX} />
            </div>
          </label>

          {/* Time + Recurrence row */}
          <div className="tdm-row">
            <label className="tdm-field tdm-field--compact">
              <span className="tdm-label">{t({ id: 'daily.todoModal.fieldTime' })}</span>
              <div className="tdm-time" data-testid="todo-time-field">
                <div className="tdm-time-wrap">
                  <button
                    type="button"
                    className={`tdm-input tdm-input--time-btn${time ? ' tdm-input--time-btn--has-value' : ''}`}
                    onClick={() => setTimePickerOpen(o => !o)}
                  >
                    {time ? formatTime12h(time) : '--:-- --'}
                  </button>
                  {time && (
                    <button
                      type="button"
                      className="tdm-time-clear"
                      onClick={() => { setTime(''); setTimePickerOpen(false); }}
                    >✕</button>
                  )}
                </div>
                {timePickerOpen && (
                  <TimeScrollPicker
                    value={time}
                    onChange={(v) => { setTime(v); }}
                    onClose={() => setTimePickerOpen(false)}
                  />
                )}
              </div>
            </label>

          </div>

          <div className="tdm-field">
            <span className="tdm-label">{t({ id: 'daily.todoModal.onComplete' })}</span>
            <div className="tdm-pill-row">
              <button
                type="button"
                className={`tdm-pill${onComplete === 'archive' ? ' tdm-pill--active' : ''}`}
                onClick={() => setOnComplete('archive')}
              >{t({ id: 'daily.todoModal.onCompleteArchive' })}</button>
              <button
                type="button"
                className={`tdm-pill${onComplete === 'delete' ? ' tdm-pill--active' : ''}`}
                onClick={() => setOnComplete('delete')}
              >{t({ id: 'daily.todoModal.onCompleteDelete' })}</button>
            </div>
          </div>

          {/* Recurrence */}
          <div className="tdm-field">
            <span className="tdm-label">{t({ id: 'daily.todoModal.repeat' })}</span>
            <div className="tdm-pill-row">
              {RECURRENCE_TYPES.map(key => (
                <button
                  key={key}
                  type="button"
                  className={`tdm-pill${recType === key ? ' tdm-pill--active' : ''}`}
                  onClick={() => setRecType(key)}
                >{t({ id: RECURRENCE_LABEL_IDS[key] })}</button>
              ))}
            </div>

            {(recType === 'weekly' || recType === 'biweekly') && (
              <div className="tdm-day-picker">
                {DAY_LABEL_IDS.map((labelId, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`tdm-day-btn${recDays.includes(i) ? ' tdm-day-btn--active' : ''}`}
                    onClick={() => toggleDay(i)}
                  >{t({ id: labelId })}</button>
                ))}
              </div>
            )}

            {recType === 'monthly' && (
              <div className="tdm-inline-field">
                <span className="tdm-label-sm">{t({ id: 'daily.todoModal.dayOfMonth' })}</span>
                <input
                  className="tdm-input tdm-input--narrow"
                  type="number"
                  min={1}
                  max={31}
                  value={recDayOfMonth}
                  onChange={e => setRecDayOfMonth(Number(e.target.value))}
                />
              </div>
            )}

            {recType === 'interval' && (
              <div className="tdm-inline-field">
                <span className="tdm-label-sm">{t({ id: 'daily.todoModal.every' })}</span>
                <input
                  className="tdm-input tdm-input--narrow"
                  type="number"
                  min={1}
                  value={recEvery}
                  onChange={e => setRecEvery(Number(e.target.value))}
                />
                <select
                  className="tdm-select"
                  value={recUnit}
                  onChange={e => setRecUnit(e.target.value as 'day' | 'week')}
                >
                  <option value="day">{t({ id: 'daily.todoModal.days' })}</option>
                  <option value="week">{t({ id: 'daily.todoModal.weeks' })}</option>
                </select>
              </div>
            )}
          </div>

      </form>
    </Modal>
  );
};

export default TodoModal;
